import React from "react";

import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import ModernModal from "../../../../common/ModernModal";
import {
  ModernFormField,
  ModernSubmitButton,
  ModernFormAlert,
} from "../../../../common/ModernForm";
import { episodeService } from "../../../../Episode/services";

interface Props {
  handleClose: () => void;
  open: boolean;
  episodeId: string;
  episodeName: string;
  onSuccess: () => void;
}

export default function RenameEpisodeDialog({
  handleClose,
  open,
  episodeId,
  episodeName,
  onSuccess,
}: Props) {
  const { mutateAsync, isError, error, isPending, isSuccess } = useMutation({
    mutationKey: ["rename-episode", episodeId],
    mutationFn: (values: { name: string }) =>
      episodeService.updateEpisode(episodeId, { name: values.name }),
  });

  if (isSuccess) {
    setTimeout(() => {
      handleClose();
      onSuccess();
    }, 1500);
  }

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title="Rename Episode"
      maxWidth="sm"
    >
      <Formik
        initialValues={{ name: episodeName }}
        validationSchema={Yup.object({
          name: Yup.string().required("Required"),
        })}
        onSubmit={(values) => mutateAsync(values)}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isError && (
                <ModernFormAlert severity="error">
                  {(error as any)?.message || "An error occurred"}
                </ModernFormAlert>
              )}

              {isSuccess && (
                <ModernFormAlert severity="success">
                  Episode renamed successfully!
                </ModernFormAlert>
              )}

              <ModernFormField
                label="Episode Name"
                name="name"
                error={!!formik.errors.name}
                helperText={formik.errors.name}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />

              <ModernSubmitButton
                label="Save"
                type="submit"
                loading={isPending}
                disabled={isPending || isSuccess}
              />
            </Box>
          </form>
        )}
      </Formik>
    </ModernModal>
  );
}
